import styled, { css } from 'styled-components';
import { rem } from 'polished';
import variables from '@styles/variables';
import Config from '@config';
import Grid from '@atoms/Grid/Grid';
import Button from '@atoms/Button/Button';
import { Variant } from '@molecules/Carousel/Carousel';
import { DotStyle } from '@helpers/carouselHelper';

export const CarouselWrapper = styled.div`
  width: 100%;
`;

export const CarouselR = styled.div`
  position: relative;
`;

export const StyledCarousel = styled.div<{ $variant: Variant }>`
  ${(props) =>
    props.$variant === 'default' &&
    css`
      border: ${rem(1)} solid ${variables.colors.mediumGray};
    `}

  ${(props) =>
    props.$variant === 'mixmatch' &&
    css`
      padding: ${rem(20)} ${rem(20)} ${rem(40)} ${rem(20)};

      ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
        border-radius: ${rem(30)};
        padding: ${rem(20)} ${rem(16)} ${rem(40)};
      }
    `}
`;

export const CarouselSlide = styled.div<{ $variant: Variant }>`
  position: relative;
  flex: 1 0 100%;
  transition: left ease 500ms;
`;

export const CarouseSlideGroup = styled(Grid)<{ $variant: Variant; $isTouch: boolean }>`
  margin: 0;
  display: flex;
  ${(props) =>
    props.$variant === 'mixmatch' &&
    css`
      display: grid;
      margin: 0 auto ${rem(2)} 0;

      ${Config.MEDIA.FROM_DESKTOP} {
        grid-template-columns: repeat(auto-fill, minmax(${rem(190)}, 1fr));
      }
    `}

  ${(props) =>
    (props.$variant === 'multisearch' || props.$variant === 'buyAllProducts') &&
    css`
      width: 100%;
      display: grid;

      // Hack to increase height by 2 px to account for content-sized product boxes (border width x 2) in multisearch etc
      padding-bottom: ${rem(2)};

      ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
        margin: 0 auto;
      }
    `}
  
  ${(props) =>
    props.$variant === 'slotpicker' &&
    css`
      // !important overrides Grid's styles which otherwise win due to specificity
      display: flex !important;
      margin: 0 !important;
      gap: ${rem(8)};

      // Padding to accommodate outline offset (2px outline + 2px offset)
      padding: ${rem(6)};

      // On touch devices, reduced padding reveals the next item as a scroll hint.
      ${props.$isTouch &&
      css`
        padding: ${rem(1)};
      `}
    `}
`;

export const AnimatedContainer = styled.div<{ $noArrowDesign: boolean }>`
  display: flex;
  position: relative;
  transition: left 0s cubic-bezier(0.65, 0, 0.35, 1);
  ${(props) =>
    props.$noArrowDesign &&
    css`
      gap: ${rem(8)};
    `}
`;

export const CarouselButton = styled(Button).attrs({ variant: 'transparent' })<{
  $hide: boolean;
  $carouselVariant: Variant;
}>`
  display: ${(props) => (props.$hide ? 'none' : 'block')};

  &:disabled {
    opacity: 0.5;
  }

  ${(props) =>
    props.$carouselVariant === 'slotpicker' &&
    css`
      position: absolute;
      top: 50%;
      z-index: ${variables.zIndex[1]};

      transform: translateY(-50%);

      &:first-of-type {
        left: ${rem(-10)};
      }

      &:last-of-type {
        right: ${rem(-10)};
      }

      &:disabled {
        display: none;
      }
    `}
`;

export const Pagination = styled.div`
  max-width: ${rem(94)};
  position: relative;
  margin-top: ${rem(10)};
`;

export const PaginationDots = styled.div`
  position: relative;
  transition: left 0s cubic-bezier(0.65, 0, 0.35, 1);
  white-space: nowrap;
`;

export const PaginationDot = styled.span<{ $state: DotStyle }>`
  width: ${(props) => rem(props.$state.size)};
  height: ${(props) => rem(props.$state.size)};
  border-radius: 100%;
  margin-left: ${rem(8)};
  display: inline-block;
  background-color: ${(props) => (props.$state.active ? variables.colors.primary : variables.colors.mediumGray)};

  &:first-child {
    margin-left: 0;
  }

  ${(props) =>
    props.$state.size === 4 &&
    css`
      margin-bottom: ${rem(2)};
    `}

  ${(props) =>
    props.$state.size === 6 &&
    css`
      margin-bottom: ${rem(1)};
    `}
`;

export const StyledCarouselHeader = styled.div<{ $variant: Variant }>`
  display: flex;
  justify-content: space-between;
  padding: ${rem(10)};
  align-items: center;
  border-bottom: ${rem(1)} solid ${variables.colors.mediumGray};

  ${Config.MEDIA.IS_MOBILE_SMALL} {
    padding-left: ${rem(20)};
  }

  ${(props) =>
    (props.$variant === 'multisearch' || props.$variant === 'buyAllProducts') &&
    css`
      padding: ${rem(10)} 0;
      border-bottom: none;

      ${Config.MEDIA.IS_MOBILE_SMALL} {
        padding-left: 0;
      }

      ${Config.MEDIA.IS_MOBILE_XSMALL} {
        padding-left: ${rem(variables.size.containerPaddingMobileSmall)};
      }
    `}

  ${(props) =>
    props.$variant === 'mixmatch' &&
    css`
      padding: 0 0 ${rem(10)} 0;
      border-bottom: none;

      ${Config.MEDIA.IS_MOBILE_SMALL} {
        padding-left: ${rem(0)};
      }
    `}
`;

export const StyledNavigationArrowsWrapper = styled.div`
  display: flex;
  gap: ${rem(15)};
`;

export const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
`;
